import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Link,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Phone,
  Email,
  LocationOn,
  Facebook,
  Instagram,
  LinkedIn,
} from '@mui/icons-material';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { label: 'Home', href: '#hero' },
    { label: 'Services', href: '#services' },
    { label: 'Our Team', href: '#about' },
    { label: 'Contact', href: '#contact' },
  ];

  const services = [
    'Back Pain Treatment',
    'Neck Pain Relief',
    'Sports Injuries',
    'Workplace Injuries',
    'Pregnancy Care',
    'Myotherapy & Remedial Massage',
  ];

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId.replace('#', ''));
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: '#1a1a1a',
        color: 'white',
        pt: 6,
        pb: 3,
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                mb: 2,
                background: 'linear-gradient(45deg, #64b5f6, #2196f3)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Health By Chiropractic
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                mb: 3,
                lineHeight: 1.6,
              }}
            >
              Expert chiropractic care in Melton, providing evidence-based treatments
              for back pain, sports injuries, and holistic wellness. Your path to
              pain-free living starts here.
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Phone sx={{ mr: 2, color: '#64b5f6' }} />
              <Link
                href="tel:(03) 9743 5510"
                sx={{ color: 'white', textDecoration: 'none' }}
              >
                (03) 9743 5510
              </Link>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Email sx={{ mr: 2, color: '#64b5f6' }} />
              <Link
                href="mailto:info@healthbychiropractic.com.au"
                sx={{ color: 'white', textDecoration: 'none' }}
              >
                info@healthbychiropractic.com.au
              </Link>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <LocationOn sx={{ mr: 2, color: '#64b5f6', mt: 0.5 }} />
              <Link
                href="https://maps.google.com/?q=477+High+Street,+Melton+VIC+3337"
                target="_blank"
                sx={{ color: 'white', textDecoration: 'none' }}
              >
                477 High Street,<br />
                Melton VIC 3337
              </Link>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, mb: 2 }}
            >
              Quick Links
            </Typography>
            {quickLinks.map((link) => (
              <Typography
                key={link.label}
                variant="body2"
                sx={{
                  mb: 1,
                  cursor: 'pointer',
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&:hover': {
                    color: '#64b5f6',
                  },
                }}
                onClick={() => scrollToSection(link.href)}
              >
                {link.label}
              </Typography>
            ))}
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, mb: 2 }}
            >
              Our Services
            </Typography>
            {services.map((service) => (
              <Typography
                key={service}
                variant="body2"
                sx={{
                  mb: 1,
                  cursor: 'pointer',
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&:hover': {
                    color: '#64b5f6',
                  },
                }}
                onClick={() => scrollToSection('#services')}
              >
                {service}
              </Typography>
            ))}
          </Grid>

          <Grid item xs={12} md={3}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, mb: 2 }}
            >
              Business Hours
            </Typography>
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 0.5 }}>
                <strong>Monday:</strong> 9:00am - 6:30pm
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 0.5 }}>
                <strong>Tuesday:</strong> 12:00pm - 7:00pm
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 0.5 }}>
                <strong>Wednesday:</strong> 9:00am - 6:30pm
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 0.5 }}>
                <strong>Thursday:</strong> Closed
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 0.5 }}>
                <strong>Friday:</strong> 9:30am - 6:00pm
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 0.5 }}>
                <strong>Saturday:</strong> 8:00am - 12:00pm
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                <strong>Sunday:</strong> Closed
              </Typography>
            </Box>

            <Typography
              variant="h6"
              sx={{ fontWeight: 600, mb: 2 }}
            >
              Follow Us
            </Typography>
            <Box>
              <IconButton
                sx={{ color: 'rgba(255, 255, 255, 0.7)', '&:hover': { color: '#64b5f6' } }}
                aria-label="Facebook"
              >
                <Facebook />
              </IconButton>
              <IconButton
                sx={{ color: 'rgba(255, 255, 255, 0.7)', '&:hover': { color: '#64b5f6' } }}
                aria-label="Instagram"
              >
                <Instagram />
              </IconButton>
              <IconButton
                sx={{ color: 'rgba(255, 255, 255, 0.7)', '&:hover': { color: '#64b5f6' } }}
                aria-label="LinkedIn"
              >
                <LinkedIn />
              </IconButton>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4, backgroundColor: 'rgba(255, 255, 255, 0.2)' }} />

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            textAlign: { xs: 'center', sm: 'left' },
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: { xs: 2, sm: 0 } }}
          >
            © {currentYear} Health By Chiropractic. All rights reserved.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Link
              href="#"
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                textDecoration: 'none',
                fontSize: '0.875rem',
                '&:hover': { color: '#64b5f6' },
              }}
            >
              Privacy Policy
            </Link>
            <Link
              href="#"
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                textDecoration: 'none',
                fontSize: '0.875rem',
                '&:hover': { color: '#64b5f6' },
              }}
            >
              Terms of Service
            </Link>
            <Link
              href="#"
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                textDecoration: 'none',
                fontSize: '0.875rem',
                '&:hover': { color: '#64b5f6' },
              }}
            >
              AHPRA Registration
            </Link>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;